"use client";

/**
 * Gets the locale from pathname string (for use in client components)
 * This is the client-safe version that doesn't use next/headers
 * Falls back to te-IN if not found (for root path or paths without locale)
 */
export const getLocaleFromPathname = (pathname: string): string => {
  // If pathname is root, default to te-IN (middleware rewrites / to /te-IN/ internally)
  if (pathname === "/") {
    return "te-IN";
  }

  // Try to extract locale from pathname (case-insensitive so TE-IN still resolves)
  const localeMatch = pathname.match(/^\/([\w]{2}-[\w]{2})(\/|$)/i);
  if (localeMatch) {
    return localeMatch[1];
  }

  // If no locale found in pathname, default to te-IN
  return "te-IN";
};

/**
 * Gets the locale prefix for URLs based on current pathname.
 * Returns empty string for root path, locale prefix for locale paths.
 * @param pathname - Current pathname from usePathname()
 * @returns Locale prefix (e.g., "/te-IN" or "") - includes leading slash if not empty
 */
export const getLocalePrefix = (pathname: string): string => {
  if (pathname === "/") {
    return "";
  }

  const localeMatch = pathname.match(/^\/([\w]{2}-[\w]{2})(\/|$)/i);
  if (localeMatch) {
    return `/${localeMatch[1]}`;
  }

  return "";
};

/**
 * UI language from leading /xx-YY in the pathname when present,
 * otherwise from API content language when the path has no locale segment.
 */
export function getAppLocaleFromPathname(
  pathname: string,
  contentLanguage?: string | null,
): AppLocale {
  const m = pathname.match(/^\/([\w]{2}-[\w]{2})(\/|$)/i);
  if (m) {
    const code = (m[1].split(/[-_]/)[0] || "te").toLowerCase();
    return code === "te" ? "te" : "en";
  }
  const cl =
    (contentLanguage ?? "").toString().trim().split(/[-_]/)[0]?.toLowerCase() || "te";
  return cl === "te" ? "te" : "en";
}

/** Typography language for a news item — prefers the article's own language over the URL locale. */
export function getArticleAppLocale(
  articleLanguage?: string | null,
  pathname?: string,
): AppLocale {
  if (articleLanguage != null && String(articleLanguage).trim() !== "") {
    const code = String(articleLanguage).split(/[-_]/)[0].toLowerCase();
    return code === "en" ? "en" : "te";
  }
  return pathname ? getAppLocaleFromPathname(pathname) : "te";
}
